import { symbol as Symbol } from '@wix/santa-editor-symbols';
import { InfoIcon, TextLabel } from '@wix/wix-base-ui';
import classnames from 'classnames';
import React, { FC, useCallback } from 'react';
import { BIOrigin } from '../../../../constants/BI';
import { SECTION_HEADER_TOOLTIP_MARGIN } from '../../../../constants/CategoryView';
import { HelpActionInitiator, useActionsDispatcher } from '../../../ActionsDispatcher';
import { useCurrentCategory } from '../../../CurrentCategoryProvider';
import {
  generateDescriptionKey,
  generateThumbnailKey,
  generateTitleKey,
  generateVideoIdKey,
} from '../../../utils/media/keyGenerator';
import { useUtils } from '../../../UtilsProvider';
import styles from './SectionHeader.scss';
import type { SectionHeaderProps } from './SectionHeader.types';

const LEARN_MORE = 'add_section_label_description_learn_more';

export const SectionHeader: FC<SectionHeaderProps> = ({
  headerText,
  title,
  disableInfoIcon = false,
  shouldTranslate = true,
  dataHook,
  className,
  style,
  helpId = '',
}) => {
  const { translate, media } = useUtils();
  const { dispatchHelpClick, dispatchPlayVideo } = useActionsDispatcher();
  const { id: categoryId } = useCurrentCategory();

  const handleClickHelp = useCallback(() => {
    dispatchHelpClick({
      helpId,
      categoryId,
      initiator: HelpActionInitiator.SectionHeader,
      biHelpParams: {
        component: generateTitleKey(title),
        learn_more: true,
        panel_name: BIOrigin.AddPanel,
        origin: BIOrigin.AddPanel,
      },
    });
  }, [dispatchHelpClick, helpId, title, categoryId]);

  const handleClickPlay = useCallback(() => {
    dispatchPlayVideo({ title });
  }, [dispatchPlayVideo, title]);

  const shouldRenderWithVideo = (sectionKey: string) =>
    media.getVideoIfAvailable(sectionKey, generateVideoIdKey(sectionKey));

  const getVideoThumbnailIfAvailable = (sectionKey: string) => {
    const videoThumbnail = generateThumbnailKey(sectionKey);
    const translatedVideoThumbnail = shouldTranslate ? translate(videoThumbnail) : videoThumbnail;

    return shouldRenderWithVideo(sectionKey) ? translatedVideoThumbnail : undefined;
  };

  const helpProps = helpId
    ? {
        linkText: translate(LEARN_MORE),
        onLinkClick: handleClickHelp,
      }
    : {};

  const videoProps = shouldRenderWithVideo(title)
    ? {
        customSymbol: <Symbol name="info_video_tooltip_icon" />,
        illustration: (
          <div className={styles.infoIconImageContainer} data-hook="section-header-info-icon-illustration">
            <img src={getVideoThumbnailIfAvailable(title)} className={styles.infoIconImage} alt="" />
            <Symbol className={styles.playVideoIcon} name="play-video" />
          </div>
        ),
        onIllustrationClick: handleClickPlay,
      }
    : {
        customSymbol: <Symbol name="infoIcon" />,
      };

  const infoIconTitle = generateTitleKey(title);
  const infoIconTranslatedTitle = shouldTranslate ? translate(infoIconTitle) : infoIconTitle;

  const infoIconText = generateDescriptionKey(title);
  const infoIconTranslatedText = shouldTranslate ? translate(infoIconText) : infoIconText;

  return (
    <div className={classnames(styles.sectionHeader, className)} data-hook={dataHook} style={style}>
      <TextLabel
        value={shouldTranslate ? translate(headerText) : headerText}
        shouldTranslate={false}
        ellipsisProps={{ disabled: true }}
        type="T07"
        className={styles.title}
        dataHook="add-panel-section-header-text"
      />

      {disableInfoIcon ? null : (
        <InfoIcon
          dataHook="section-header-info-icon"
          className={classnames(styles.sectionInfoIcon, styles.sectionAddPanelInfoIcon)}
          title={infoIconTranslatedTitle}
          text={infoIconTranslatedText}
          shouldTranslate={false}
          shouldCloseOnMouseClick
          closeOnContentMouseClick
          alignment="RIGHT"
          tooltipMarginLeft={SECTION_HEADER_TOOLTIP_MARGIN}
          tooltipMarginRight={SECTION_HEADER_TOOLTIP_MARGIN}
          tooltipMaxWidth={255}
          {...helpProps}
          {...videoProps}
        />
      )}
    </div>
  );
};
